import { ProjectFeatureFlags, SerializedAppResource } from '@journeyapps-platform/types-accounts-hub';
import { TemplateLanguage } from '@journeyapps-platform/types-alpha';

export function isJourneyEmail(email: string): boolean {
  if (!email) {
    return false;
  }
  return /^.*@journeyapps\.com$/.test(email);
}

export enum ErrorCodes {
  DUPLICATE_EMAIL = 'RESOURCE_CONFLICT',
  PHONE_NUMBER_VALIDATION_ERROR = 'PHONE_NUMBER_VALIDATION_ERROR'
}

export interface ProductTip {
  title: string;
  description: string;
}

export enum Product {
  POWERSYNC = 'powersync',
  APP = 'app'
}

export enum IDE {
  OXIDE = 'oxide',
  COLLIDE = 'collide'
}

export interface ProductConfig {
  product: Product;
  ide: IDE;
  product_name: string;
  new_app_features: ProjectFeatureFlags[];
  urlParam: string;
  terms_of_service_url: string;
  privacy_policy: string;
  signup_secondary_label: string | null;
  languages: TemplateLanguage[];
  tips: ProductTip[];
  send_welcome_email: boolean;
}

export interface EnvCommon {
  OXIDE_URL: string;
  COLLIDE_URL: string;
}

export const PRODUCT_CONFIG_MAP: { [t in Product]: ProductConfig } = {
  [Product.APP]: {
    product: Product.APP,
    product_name: 'JourneyApps Platform',
    ide: IDE.OXIDE,
    new_app_features: [ProjectFeatureFlags.APP],
    urlParam: 'app',
    terms_of_service_url: 'https://journeyapps.com/terms-of-service/',
    privacy_policy: 'https://journeyapps.com/privacy-policy/',
    signup_secondary_label: 'Enjoy a 14 day free trial',
    languages: [TemplateLanguage.JavaScript, TemplateLanguage.TypeScript],
    send_welcome_email: true,
    tips: [
      {
        title: 'Did you know?',
        description: 'Pressing _Shift+CMD+P_ in OXIDE calls\nthe Command Palette'
      }
    ]
  },
  [Product.POWERSYNC]: {
    product: Product.POWERSYNC,
    product_name: 'PowerSync',
    ide: IDE.COLLIDE,
    new_app_features: [ProjectFeatureFlags.POWERSYNC],
    urlParam: 'powersync',
    terms_of_service_url: 'https://www.powersync.com/terms-of-service',
    privacy_policy: 'https://www.powersync.com/privacy-policy',
    signup_secondary_label: null,
    languages: [TemplateLanguage.PowerSync],
    send_welcome_email: false,
    tips: [
      {
        title: 'Offline-first architecture',
        description: 'PowerSync enables an offline-first architecture for real-time reactive apps'
      },
      {
        title: 'Drop-in sync layer',
        description: 'PowerSync is a drop-in sync layer for your existing Postgres and backend.'
      }
    ]
  }
};

export const APP_FEATURES = {
  [ProjectFeatureFlags.APP]: 'JourneyApps Platform',
  [ProjectFeatureFlags.POWERSYNC]: 'PowerSync'
};

export interface GetAppUrlParams {
  env: EnvCommon;
  app: SerializedAppResource;
}

export interface GetIDEUrlParams {
  env: EnvCommon;
  ide: IDE;
}

export const getIDEUrl = (params: GetIDEUrlParams) => {
  switch (params.ide) {
    case IDE.COLLIDE:
      return params.env.COLLIDE_URL;
    case IDE.OXIDE:
      return params.env.OXIDE_URL;
  }
};

export const getAppUrl = (params: GetAppUrlParams) => {
  const product = getProductBasedOnFeatures(params.app.features);
  const baseURL = getIDEUrl({
    ide: PRODUCT_CONFIG_MAP[product].ide,
    env: params.env
  });
  return `${baseURL}/org/${params.app.org_id}/app/${params.app.id}`;
};

export const getProductBasedOnFeatures = (features: ProjectFeatureFlags[]) => {
  if (features.indexOf(ProjectFeatureFlags.POWERSYNC) !== -1) {
    return Product.POWERSYNC;
  }
  return Product.APP;
};
